import { Color, Spacing } from '@persona-id/design-tokens';
import { EyeIcon, EyeOffIcon } from '@persona-id/design-tokens/icons';
import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import GraphVertexIcon from 'dashboard/components/GraphVertexIcon/GraphVertexIcon';
import { DatumTypeToken, NodeId, nodeIdToHashable } from 'dashboard/models/graph';
import { NodeRenderConfigLookupFn } from 'dashboard/models/graph-explorer';

interface GraphExplorerPanelRowProps {
  nodeId: NodeId;
  label: ReactNode;
  dimmed: boolean;
  selected: boolean;
  highlighted: boolean;
  handleClick: (nodeId: NodeId, selected: boolean) => void;
  handleDimClick: (nodeId: NodeId, dimmed: boolean) => void;
  handleHover: (nodeId: NodeId, hover: boolean) => void;
  datumTypeToken: DatumTypeToken;
  getNodeRenderConfig: NodeRenderConfigLookupFn;
}

const IconButtonContainer = styled('div')`
  padding-right: 9px;
  min-width: ${Spacing[2]};
  margin-left: auto;
  margin-right: 0;

  svg {
    vertical-align: text-top;
  }
`;

const PositionedGraphVertexIcon = styled(GraphVertexIcon)`
  margin-top: auto;
  margin-bottom: auto;
`;

const LabelContainer = styled('span')`
  text-overflow: ellipsis;
  max-width: 265px;
  overflow: hidden;
  margin-left: 6px;
`;

const RowBody = styled('div')`
  display: flex;
  flex-direction: row;
  padding-left: 4px;
`;

interface PanelItemContainerProps {
  dimmed: boolean;
  active: boolean;
  activeHighlight: boolean;
}

const PanelItemContainer = styled('div')<PanelItemContainerProps>`
  padding: 2px 2px 2px 23px;
  cursor: pointer;
  text-overflow: ellipsis;

  overflow-y: auto;
  overflow-x: hidden;
  color: ${(props) => (props.dimmed ? Color.CoreSlate700 : '')};
  background-color: ${(props) => (props.active ? Color.CorePurple100 : '')};
  border: ${(props) =>
    props.activeHighlight ? `1px solid ${Color.CorePurple800}}` : '1px solid transparent'};
  p {
    cursor: pointer;
  }
`;

const StyledEyeIcon = styled(EyeIcon)<{ hover: boolean }>`
  visibility: ${(props) => (props.hover ? 'visible' : 'hidden')};
`;

const GraphExplorerPanelRow = ({
  nodeId,
  label,
  dimmed,
  highlighted,
  selected,
  handleClick,
  handleDimClick,
  handleHover,
  datumTypeToken,
  getNodeRenderConfig,
}: GraphExplorerPanelRowProps) => {
  const [activeHighlight, setActiveHighlight] = useState(highlighted);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    setHover(highlighted);
    setActiveHighlight(highlighted);
  }, [highlighted]);

  return (
    <PanelItemContainer
      id={nodeIdToHashable(nodeId)}
      dimmed={dimmed}
      active={selected}
      activeHighlight={activeHighlight}
      onMouseEnter={(e) => {
        e.stopPropagation();
        setHover(true);
        handleHover(nodeId, true);
        setActiveHighlight(true);
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        setHover(false);
        handleHover(nodeId, false);
        setActiveHighlight(false);
      }}
      onClick={(e) => {
        handleClick(nodeId, !selected);
        e.stopPropagation();
      }}
    >
      <RowBody>
        <PositionedGraphVertexIcon
          version='v2'
          datumTypeToken={datumTypeToken}
          backgroundNone={true}
          dimmed={dimmed}
          nodeRenderConfig={getNodeRenderConfig(datumTypeToken)}
        />
        <LabelContainer>{label}</LabelContainer>
        <IconButtonContainer
          role='button'
          id={`button-${nodeIdToHashable(nodeId)}`}
          onClick={(e) => {
            handleDimClick(nodeId, !dimmed);
            e.stopPropagation();
          }}
        >
          {dimmed && <EyeOffIcon />}
          {!dimmed && <StyledEyeIcon hover={hover} />}
        </IconButtonContainer>
      </RowBody>
    </PanelItemContainer>
  );
};

export default GraphExplorerPanelRow;
